import { ILocaProcessor } from "./processors/api/ILocaProcessor";
import { LocalizationModuleError } from "./errors/LocalizationModuleError";
import { LocalizationModuleErrorConstants } from "./errors/LocalizationModuleErrorConstants";
import { ILocalizeService } from "./service/api/ILocalizeService";
import { ILocalizationService } from "./service/api/ILocalizationService";
import { LocalizationService } from "./service/impl/LocalizationService";
import { LocalizeService } from "./service/impl/LocalizeService";
import { Localize } from "./utils/Localize";
import { Localization } from "./utils/Localization";
import { LanguageVO } from "./vos/LanguageVO";
/**
 * <p>
 *     <strong>Localization Module</strong>
 * </p>
 *
 * <p>
 *     Localization in general means that you translate some data (text, number, date etc) from one local
 *     representation into another. In this context local means local to a country or a region.
 *     For example, date and time have different presentation in different countries,
 *     in Germany for the most of the time you will see this representation: dd.MM.yyyy for date and
 *     24-hour-notation for time, but in USA you will see: M/d/yyyy and 12-hour time notation.
 *     Another example is texts, at the time of writing this wiki there was not any reasonable way to translate
 *     correctly a text written in one language into another. For text translation the most common approach is to
 *     use parallel data tables (e.g. json files) with identical keys but with different data.
 *     To translate a text you just pass a key to localization method and receive a localized text.
 * </p>
 *
 * <p>
 *     The <code>LocalizationModule</code> provides a simplified way to setup and use the localization
 *     feature in your game.
 * </p>
 *
 * <p>
 *     The actual logic of the localization is encapsulated inside a processor ie:
 *
 *     <ul>
 *         <li>FLASH processor.</li>
 *         <li>ICU processor</li>
 *          <li>i18next processor</li>
 *          <li>Globalize processor</li>
 *     </ul>
 * </p>
 *
 *  <p>
 *     The <strong>GLOBALIZE processor</strong> use the implementation provided by https://www.npmjs.com/package/globalize
 * </p>
 *
 * <p>
 *     The <strong>FLASH processor</strong> use the implementation provided by the <strong>Adobe</strong>
 *     (package <strong>flash.globalization</strong>) to localize texts, dates and numbers. Take a look on our
 *     <a target="_blank" href="https://sites.google.com/a/goodgamestudios.com/tech-wiki/game-technology/client-department/core-projects/toolbox/features/localization">Localization Wiki Page</a>
 *     to know detailed information about our current implementation of the this processor.
 * </p>
 *
 * <p>
 *     The <strong>ICU processor</strong> use the implementation provided by the <strong>ICU - International Components for Unicode</strong>
 *     project to localize texts, dates and numbers. Take a look on our
 *     <a target="_blank" href="https://sites.google.com/a/goodgamestudios.com/tech-wiki/game-technology/client-department/core-projects/x-platform/localization">ICU Localization Wiki Page</a>
 *     to know detailed information about our current implementation of the this processor.
 * </p>
 *
 * <p>
 *     By default the <strong>LocalizationModule</strong> use a <strong>case sensitive</strong> approach for the
 *     <strong>textId's</strong> so keys like <strong>text</strong>, <strong>Text</strong> and <strong>TEXT</strong>
 *     will be treated as different keys.
 * </p>
 *
 * <p>
 *     If you want to use a <strong>case insensitive</strong> approach you have to set the <code>isCaseSensitive</code>
 *     value on the <code>LocalizationModuleSettings</code> to <code>false</code>. Please note that keys like
 *     <strong>text</strong>, <strong>Text</strong> and <strong>TEXT</strong> will be treated as the same key,
 *     and if you have these different definitions on your <strong>json</strong> file, the last definition will override
 *     the prior ones.
 * </p>
 *
 * <p>
 *     The <code>LocalizationModule</code> provide two external interfaces exposing the public API of the module:
 * </p>
 *
 * <p>
 *     <ul>
 *         <li><code>ILocalizationService</code></li>
 *         <li><code>ILocalizeService</code></li>
 *     </ul>
 * </p>
 *
 * <p>
 *     The <code>ILocalizationService</code> interface expose methods that can be used to initialize and setup the
 *     localization package. Use this interface in order to initialize the setService, define the current language and
 *     locale and set the definition of the texts. This interface will be installed on game context and will be
 *     accessible through injection.
 * </p>
 *
 * <p>
 *     The <code>ILocalizeService</code> interface expose methods that can be used localize to texts, dates and numbers.
 *     Use this interface in order to localize the strings that will be showed to the players. This interface will
 *     be installed on game context and will be accessible through injection.
 * </p>
 *
 * <p>
 *     For the cases where is not possible to get a instance of the services through injection, we provide two wrappers
 *     that can be used:
 *
 *     <ul>
 *         <li>Localization</li>
 *         <li>Localize</li>
 *     </ul>
 * </p>
 *
 * <p>
 *     These wrappers have static methods that are exposing the methods provided by the two public interfaces.
 *     In any case, you have to have sure that the <strong>LocalizationModule</strong> is properly installed into
 *     game context in order to use the wrappers, otherwise a error will be throw.
 * </p>
 *
 * @see com.goodgamestudios.core.localization.config.LocalizationModuleSettings
 * @see com.goodgamestudios.core.localization.errors.LocalizationModuleError
 * @see com.goodgamestudios.core.localization.errors.LocalizationModuleErrorConstants
 // tslint:disable-next-line:max-line-length
 * @see https://sites.google.com/a/goodgamestudios.com/tech-wiki/game-technology/client-department/core-projects/toolbox/features/localization
 *
 * @example The following code installs the LocalizationModule enabling the FLASH processor using a case sensitive approach:
 *
 * <listing version="3.0">
 *
 *     // Setup the settings choosing the flash processor
 *     var moduleSettings:LocalizationModuleSettings = new LocalizationModuleSettings( GlobalizeProcessor );
 *     var locaModule = new LocalizationModule(moduleSettings);
 *     //  add the module to a global var or to a servicelocator or whatever... in order to have it accessible
 *
 * </listing>
 *
 */
export class LocalizationModule {
    private _localizeService: ILocalizeService;
    private _localizationService: ILocalizationService;

    /**
     * Creates a instance of the <code>LocalizationModule</code> and configure the module according to the settings
     * defined by game.
     *
     * @param moduleSettings Settings of the module, must be provided and can't be null.
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_MODULE_SETTINGS_NOT_PROVIDED</code>:
     * ModuleSettings is mandatory and can't be null.
     *
     * @throws com.goodgamestudios.core.localization.errors.LocalizationModuleError With error code
     * <code>LocalizationModuleErrorConstants.CODE_PROCESSOR_CLASS_IS_INVALID</code>:
     * Processor Type is invalid.
     */
    constructor(private _processor: ILocaProcessor, private setupGlobalHelpers: boolean = false) {
        this._localizationService = new LocalizationService(_processor);
        this._localizeService = new LocalizeService(_processor, this._localizationService);
        if (setupGlobalHelpers) {
            Localize.setService(this._localizeService);
            Localization.setService(this._localizationService);
        }
    }

    public initialize(languageVO: LanguageVO, texts: any, cldrJSONData?: any, usePipe: boolean = false): void {
        this._localizationService.initialize(languageVO, texts, cldrJSONData, usePipe);
    }

    public setLanguageAsync(language: LanguageVO, texts: any): Promise<any> {
        return this._localizationService.setLanguageAsync(language, texts);
    }

    public setTexts(texts: any): void {
        this._localizationService.setTexts(texts);
    }

    get localizationService(): ILocalizationService {
        return this._localizationService;
    }

    get localizeService(): ILocalizeService {
        return this._localizeService;
    }

    get processorType() {
        return this._processor.processorType;
    }
}
